import React from 'react';
import { GrCircleAlert } from 'react-icons/gr';
import SanityImage from 'gatsby-plugin-sanity-image';

import { cn } from '@rsc/tailwind-base/utils';
import type { SanityBlockBuilderImage } from '@rsc/models/gatsby-graphql-types';

interface ImageProps {
  alt?: string;
  className?: string;
  /** The aspect ratio of the image */
  ratio?: number;
  /** A Sanity image object */
  sanityImage?: SanityBlockBuilderImage;
  sanityRole?: string;
  src?: string;
  /** The basis width for generating Sanity image srcSet */
  srcSetBasisWidth?: number;
  width?: string;
}

const Image = ({
  alt = 'image',
  className = '',
  ratio = 1,
  sanityImage = null,
  sanityRole = '',
  src = process.env.GATSBY_SANITY_FALLBACK_IMAGE_URL,
  srcSetBasisWidth = 600,
  width,
}: ImageProps) => {
  if (sanityImage && !sanityImage?.asset) return null;

  // To ensure the validity of the Sanity image object
  if (sanityImage?.asset?._id) {
    const { alt: sanityAlt, asset, crop, hotspot } = sanityImage;

    return (
      <div
        className={cn('flex overflow-hidden [&>img]:object-cover', className)}
        style={{
          alignItems: ratio < 1 ? 'stretch' : 'center',
          aspectRatio: ratio,
          height: ratio === 1 ? width : null,
          width,
        }}
      >
        <SanityImage
          alt={sanityAlt ?? alt ?? 'Image'}
          asset={asset}
          crop={crop}
          height={ratio === 1 ? srcSetBasisWidth : null}
          hotspot={hotspot?.x && hotspot?.y ? hotspot : null}
          role={sanityRole}
          sizes={`${srcSetBasisWidth}px`}
          width={srcSetBasisWidth}
        />
      </div>
    );
  }

  return (
    <div
      className={cn('flex overflow-hidden', className)}
      style={{
        alignItems: ratio < 1 ? 'stretch' : 'center',
        aspectRatio: ratio,
        width,
      }}
    >
      {src || sanityImage?.asset?.url ? (
        <img
          alt={alt}
          className="object-cover self-stretch"
          role={alt ? 'img' : 'presentation'}
          src={sanityImage?.asset?.url || src}
        />
      ) : (
        <div className="flex align-center justify-center bg-[#bcbcbc] w-full h-full">
          <GrCircleAlert className="w-8 h-auto opacity-20" />
        </div>
      )}
    </div>
  );
};

export default Image;
